import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Card, Col, Form, ListGroup, Modal, Row } from 'react-bootstrap';
import GoogleMapReact from 'google-map-react';
import {
  FaBell,
  FaBoxOpen,
  FaBuilding,
  FaCamera,
  FaCheckCircle,
  FaClock,
  FaDownload,
  FaExclamationTriangle,
  FaFileContract,
  FaHistory,
  FaMapMarkedAlt,
  FaMapMarkerAlt,
  FaMicrochip,
  FaSatelliteDish,
  FaSearch,
  FaShieldAlt,
  FaSignal,
  FaTachometerAlt,
  FaThermometerHalf,
  FaTimes,
  FaTint,
  FaTools,
  FaUserCircle
} from 'react-icons/fa';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip as ChartTooltip
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import moment from 'moment';
import 'moment/locale/pt-br';
import Page from '../../components/Page/Page';
import Headers from '../../components/Headers/Headers';
import Reload from '../../components/reload/reload';
import MapLoader from '../../components/MapLoader/MapLoader';
import ApiGServer from '../../services/ApiGServer';
import ApiMServer from '../../services/ApiMServer';
import Api from '../../services/Api';

ChartJS.register(ArcElement, ChartTooltip, Legend, CategoryScale, LinearScale, BarElement);

const HUMIDITY_MIN = 13;
const HUMIDITY_MAX = 15;
const HUMIDITY_DANGER_MIN = 8;
const HUMIDITY_DANGER_MAX = 20;
const TEMPERATURE_MIN = 20;
const TEMPERATURE_MAX = 30;
const TEMPERATURE_DANGER_MIN = 12;
const TEMPERATURE_DANGER_MAX = 35;

const BRAND_COLORS = {
  primaryDark: '#1c4220',
  primary: '#8fad15',
  primaryLight: '#f0f7e6',
  success: '#28a745',
  warning: '#e6a800',
  danger: '#ff8c00',
  dangerLight: '#ffa733',
  graphite: '#3c4042',
  muted: '#6c757d',
  softMuted: '#adb5bd',
  surface: '#fbfcf8',
  surfaceMuted: '#f4f7ee',
  white: '#ffffff'
};

const STATUS_META = {
  ok: {
    label: 'Dentro da faixa',
    short: 'Normal',
    color: BRAND_COLORS.success,
    bg: 'rgba(40, 167, 69, 0.16)',
    border: 'rgba(40, 167, 69, 0.45)'
  },
  attention: {
    label: 'Atencao',
    short: 'Atencao',
    color: BRAND_COLORS.warning,
    bg: 'rgba(230, 168, 0, 0.16)',
    border: 'rgba(230, 168, 0, 0.45)'
  },
  danger: {
    label: 'Fora da faixa',
    short: 'Critico',
    color: BRAND_COLORS.danger,
    bg: 'rgba(255, 140, 0, 0.16)',
    border: 'rgba(255, 140, 0, 0.45)'
  },
  nodata: {
    label: 'Sem leitura',
    short: 'Sem sinal',
    color: BRAND_COLORS.muted,
    bg: 'rgba(108, 117, 125, 0.12)',
    border: 'rgba(108, 117, 125, 0.32)'
  }
};

const periodOptions = [
  { key: 'today', label: 'Hoje' },
  { key: 'week', label: '7 dias' },
  { key: 'month', label: '30 dias' },
  { key: '6months', label: '6 meses' },
  { key: '1year', label: 'Ano' },
  { key: 'all', label: 'Tudo' }
];

const statusFilters = [
  { key: 'all', label: 'Todos' },
  { key: 'ok', label: 'Dentro da faixa' },
  { key: 'attention', label: 'Atencao' },
  { key: 'danger', label: 'Fora da faixa' },
  { key: 'nodata', label: 'Sem leitura' }
];

const MapMarker = (props) => {
  const { $hover, $getDimensions, $dimensionKey, $geoService, $onMouseAllow, $prerender, children, ...rest } = props; // eslint-disable-line no-unused-vars
  return <div {...rest}>{children}</div>;
};

const getNumericValue = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined || value === '') continue;
    const number = Number(String(value).replace(',', '.'));
    if (Number.isFinite(number)) return number;
  }
  return null;
};

const normalizeMeasurement = (status = {}) => {
  const source =
    status.medida ||
    status.measurement ||
    status.lastMeasurement ||
    status.lastMedida ||
    status.unlockData?.medida ||
    status.unlockData?.measurement ||
    status.sensorData?.measurements ||
    {};

  return {
    humidity: getNumericValue(source.humidity, source.umidade, status.humidity, status.umidade),
    temperature: getNumericValue(source.temperature, source.temperatura, status.temperature, status.temperatura),
    updatedAt:
      source.updatedAt ||
      source.createdAt ||
      status.updatedAt ||
      status.createdAt ||
      status.unlockData?.updatedAt ||
      null,
    raw: source
  };
};

const getReadingState = (measurement) => {
  const humidity = measurement?.humidity;
  const temperature = measurement?.temperature;

  if (humidity === null && temperature === null) {
    return { key: 'nodata', ...STATUS_META.nodata };
  }

  const humidityDanger = humidity !== null && (humidity < HUMIDITY_DANGER_MIN || humidity > HUMIDITY_DANGER_MAX);
  const temperatureDanger =
    temperature !== null && (temperature < TEMPERATURE_DANGER_MIN || temperature > TEMPERATURE_DANGER_MAX);

  if (humidityDanger || temperatureDanger) {
    return { key: 'danger', ...STATUS_META.danger };
  }

  const humidityOk = humidity === null || (humidity >= HUMIDITY_MIN && humidity <= HUMIDITY_MAX);
  const temperatureOk = temperature === null || (temperature >= TEMPERATURE_MIN && temperature <= TEMPERATURE_MAX);

  if (humidityOk && temperatureOk) {
    return { key: 'ok', ...STATUS_META.ok };
  }

  return { key: 'attention', ...STATUS_META.attention };
};

const parseDaysToActivate = (value) => {
  const parsed = parseInt(value, 16);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatNumber = (value, decimals = 0) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '--';
  return Number(value).toLocaleString('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

const formatDuration = (date, fallback = 'Sem registro') => {
  if (!date) return fallback;
  const momentDate = moment(date);
  if (!momentDate.isValid()) return fallback;
  return momentDate.fromNow();
};

const getInitials = (value = '') => {
  const normalized = String(value).trim();
  if (!normalized) return 'SG';
  return normalized
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('');
};

const getStoredUnits = () => {
  const parseJSON = (key, fallback) => {
    try {
      const value = localStorage.getItem(key);
      return value ? JSON.parse(value) : fallback;
    } catch {
      return fallback;
    }
  };

  const units = parseJSON('units', []);
  const cooperative = parseJSON('cooperative', null);
  const normalizedUnits = Array.isArray(units) ? units.filter(Boolean) : [];

  if (cooperative?._id && !normalizedUnits.some(unit => unit?._id === cooperative._id)) {
    return [cooperative, ...normalizedUnits];
  }

  return normalizedUnits;
};

const COMPANY_VERSION_COLORS = [
  '#1c4220',
  '#8fad15',
  '#28a745',
  '#3c4042',
  '#605e5c',
  '#e6a800',
  '#ff8c00',
  '#6c757d'
];

const SERIAL_TYPE_RULES = [
  { test: serial => serial.startsWith('PRAR'), label: 'Secador (PRAR)' },
  { test: serial => serial.startsWith('SBAG'), label: 'Silo bolsa (SBAG)' },
  { test: serial => serial.includes('SEMT'), label: 'SEMT' },
  { test: serial => serial.includes('SIMT'), label: 'SIMT' },
  { test: serial => serial.includes('BIMT'), label: 'BIMT' },
  { test: serial => serial.includes('CPMT'), label: 'CPMT' },
  { test: serial => serial.includes('EEMT'), label: 'EEMT' },
  { test: serial => serial.includes('ALRM'), label: 'Alarme (ALRM)' },
  { test: serial => serial.includes('RODZ'), label: 'Rodízio (RODZ)' }
];

const getEquipmentVersionFromSerial = serial => {
  const normalized = (serial || '').toString().trim().toUpperCase();
  if (!normalized) return 'Sem serial';

  const matchedRule = SERIAL_TYPE_RULES.find(rule => rule.test(normalized));
  return matchedRule?.label || 'Outros';
};

const getSerialCompanyMap = () => {
  const map = {};
  getStoredUnits().forEach(unit => {
    const label = unit?.name || unit?.companyName || 'Sem empresa';
    const serials = Array.isArray(unit?.equipments) ? unit.equipments.filter(Boolean) : [];
    serials.forEach(serial => {
      map[serial] = label;
    });
  });
  return map;
};

const averageValues = values => {
  if (!values.length) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const computeUnitMeasurementAverages = linkedCards => {
  const humidities = linkedCards.map(card => card.measurement.humidity).filter(value => value !== null);
  const temperatures = linkedCards.map(card => card.measurement.temperature).filter(value => value !== null);

  return {
    avgHumidity: averageValues(humidities),
    avgTemperature: averageValues(temperatures),
    humiditySamples: humidities.length,
    temperatureSamples: temperatures.length,
    equipmentCount: linkedCards.length,
    onlineCount: linkedCards.filter(card => card.isOnline).length
  };
};

const buildSelectedUnitContext = (selectedSerial, equipmentCards) => {
  if (!selectedSerial) return null;

  const cardsBySerial = equipmentCards.reduce((acc, card) => {
    if (card.serial) acc[card.serial] = card;
    return acc;
  }, {});

  const storedUnits = getStoredUnits();
  const matchedUnit = storedUnits.find(unit => {
    const unitSerials = Array.isArray(unit?.equipments) ? unit.equipments.filter(Boolean) : [];
    return unitSerials.includes(selectedSerial);
  });

  if (matchedUnit) {
    const unitSerials = Array.isArray(matchedUnit.equipments) ? matchedUnit.equipments.filter(Boolean) : [];
    const linkedCards = unitSerials.map(serial => cardsBySerial[serial]).filter(Boolean);
    const selectedCard = linkedCards.find(card => card.serial === selectedSerial) || linkedCards[0] || null;

    if (!selectedCard) return null;

    const averages = computeUnitMeasurementAverages(linkedCards);
    const unitReading = getReadingState({
      humidity: averages.avgHumidity,
      temperature: averages.avgTemperature
    });

    return {
      unitName: matchedUnit.name || matchedUnit.companyName || 'Unidade',
      city:
        matchedUnit.address?.city?.longname ||
        matchedUnit.address?.city?.shortname ||
        matchedUnit.city?.longname ||
        matchedUnit.city?.shortname ||
        matchedUnit.city ||
        selectedCard.city,
      state: matchedUnit.address?.state?.shortname || matchedUnit.state?.shortname || matchedUnit.state || selectedCard.state,
      selectedCard,
      linkedCards,
      reading: unitReading,
      ...averages
    };
  }

  const selectedCard = cardsBySerial[selectedSerial];
  if (!selectedCard) return null;

  const averages = computeUnitMeasurementAverages([selectedCard]);

  return {
    unitName: selectedCard.serial,
    city: selectedCard.city,
    state: selectedCard.state,
    selectedCard,
    linkedCards: [selectedCard],
    reading: getReadingState({
      humidity: averages.avgHumidity,
      temperature: averages.avgTemperature
    }),
    ...averages
  };
};

const DashboardCompany = () => {
  const [loading, setLoading] = useState(true);
  const [equipments, setEquipments] = useState([]);
  const [equipmentsStatus, setEquipmentsStatus] = useState([]);
  const [responsibleDetails, setResponsibleDetails] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('all');
  const [selectedSerial, setSelectedSerial] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [chartModal, setChartModal] = useState({ show: false, title: '', items: [] });
  const [modalSearchTerm, setModalSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const focusPanelRef = useRef(null);
  const tableClickTimeoutRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const storedSerials = JSON.parse(localStorage.getItem('equipment')) || [];

      if (storedSerials.length === 0) {
        setEquipments([]);
        setEquipmentsStatus([]);
        return;
      }

      const { data: details } = await ApiGServer.post('/equipment/filter', { filter: storedSerials });
      const { data: statuses } = await Api.post('/api/v1/lastbenchone', { equipments: storedSerials });
      const equipmentsDetails = Array.isArray(details) ? details : [];

      const uniqueEmails = [...new Set(equipmentsDetails.map(e => e.lastEmailActivation).filter(Boolean))];
      if (uniqueEmails.length > 0) {
        try {
          const { data: userData } = await ApiMServer.post('/user/v1/details-by-email', {
            emails: uniqueEmails,
            fields: ['name', 'email', 'cpfcnpj', 'fone']
          });

          const detailsMap = {};
          if (Array.isArray(userData)) {
            userData.forEach(user => {
              if (user?.email) detailsMap[user.email.toLowerCase()] = user;
            });
          }
          setResponsibleDetails(detailsMap);
        } catch (error) {
          console.error('Erro ao buscar detalhes dos responsaveis:', error);
        }
      }

      setEquipments(equipmentsDetails);
      setEquipmentsStatus(Array.isArray(statuses) ? statuses : []);
    } catch (error) {
      console.error('Erro ao buscar dados do dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(
    () => () => {
      if (tableClickTimeoutRef.current) clearTimeout(tableClickTimeoutRef.current);
    },
    []
  );

  const statusBySerial = useMemo(() => {
    const map = {};
    equipmentsStatus.forEach(status => {
      if (status?.serialnumber) map[status.serialnumber] = status;
    });
    return map;
  }, [equipmentsStatus]);

  const serialCompanyMap = useMemo(() => getSerialCompanyMap(), [equipments]);

  const equipmentCards = useMemo(() => {
    return equipments.map(equipment => {
      const status = statusBySerial[equipment.serialnumber] || {};
      const measurement = normalizeMeasurement(status);
      const reading = getReadingState(measurement);
      const emailKey = equipment.lastEmailActivation?.toLowerCase();
      const responsible = responsibleDetails[emailKey] || {};
      const city = equipment.address?.city?.longname || equipment.address?.city?.shortname || 'Local nao informado';
      const state = equipment.address?.state?.shortname || 'N/A';
      const company =
        serialCompanyMap[equipment.serialnumber] ||
        equipment.company?.name ||
        equipment.companyName ||
        equipment.empresa ||
        'Sem empresa';
      const lat = getNumericValue(equipment.lat);
      const lng = getNumericValue(equipment.lng);

      return {
        serial: equipment.serialnumber,
        rawEquipment: equipment,
        status,
        measurement,
        reading,
        responsible,
        company,
        city,
        state,
        lat,
        lng,
        hasCoordinates: lat !== null && lng !== null && lat !== 0 && lng !== 0,
        plan: equipment.plan || 'Sem plano',
        mode: equipment.mode || 'N/A',
        version: getEquipmentVersionFromSerial(equipment.serialnumber),
        daysLeft: parseDaysToActivate(equipment.daystoactivate),
        isOnline: status.status === 'online',
        lastUpdate: measurement.updatedAt || equipment.updatedAt || equipment.createdAt,
        resources: {
          tray: equipment.tray === true,
          face: equipment.scan_face === true,
          tracking: equipment.full_tracking === true
        }
      };
    });
  }, [equipments, statusBySerial, responsibleDetails, serialCompanyMap]);

  useEffect(() => {
    if (!selectedSerial && equipmentCards.length > 0) {
      setSelectedSerial(equipmentCards[0].serial);
    }
  }, [equipmentCards, selectedSerial]);

  const visibleCards = useMemo(() => {
    const search = searchTerm.trim().toUpperCase();
    const now = moment();

    return equipmentCards.filter(card => {
      const inStatus = statusFilter === 'all' || card.reading.key === statusFilter;
      const lastUpdate = card.lastUpdate ? moment(card.lastUpdate) : null;
      const inPeriod =
        periodFilter === 'all' ||
        (lastUpdate?.isValid() &&
          ((periodFilter === 'today' && lastUpdate.isSame(now, 'day')) ||
            (periodFilter === 'week' && !lastUpdate.isBefore(now.clone().subtract(7, 'days'))) ||
            (periodFilter === 'month' && !lastUpdate.isBefore(now.clone().subtract(30, 'days'))) ||
            (periodFilter === '6months' && !lastUpdate.isBefore(now.clone().subtract(180, 'days'))) ||
            (periodFilter === '1year' && !lastUpdate.isBefore(now.clone().subtract(365, 'days')))));

      if (!inStatus || !inPeriod) return false;
      if (!search) return true;

      const searchable = [
        card.serial,
        card.city,
        card.state,
        card.plan,
        card.mode,
        card.rawEquipment.lastEmailActivation,
        card.responsible.name,
        card.responsible.cpfcnpj,
        card.responsible.fone
      ]
        .filter(Boolean)
        .join(' ')
        .toUpperCase();

      return searchable.includes(search);
    });
  }, [equipmentCards, searchTerm, statusFilter, periodFilter]);

  const selectedCard = useMemo(() => {
    return equipmentCards.find(card => card.serial === selectedSerial) || visibleCards[0] || equipmentCards[0] || null;
  }, [equipmentCards, selectedSerial, visibleCards]);

  const selectedUnitContext = useMemo(
    () => buildSelectedUnitContext(selectedSerial, equipmentCards),
    [selectedSerial, equipmentCards]
  );

  const stats = useMemo(() => {
    const total = equipmentCards.length;
    const statusCounts = equipmentCards.reduce(
      (acc, card) => {
        acc[card.reading.key] += 1;
        return acc;
      },
      { ok: 0, attention: 0, danger: 0, nodata: 0 }
    );
    const humidities = equipmentCards.map(card => card.measurement.humidity).filter(value => value !== null);
    const temperatures = equipmentCards.map(card => card.measurement.temperature).filter(value => value !== null);
    const online = equipmentCards.filter(card => card.isOnline).length;
    const blocked = equipmentCards.filter(card => card.daysLeft === 0).length;
    const withCoordinates = equipmentCards.filter(card => card.hasCoordinates).length;
    const updatedDates = equipmentCards
      .map(card => card.lastUpdate)
      .filter(Boolean)
      .map(date => moment(date))
      .filter(date => date.isValid());
    const lastUpdate = updatedDates.length > 0 ? moment.max(updatedDates) : null;

    return {
      total,
      online,
      offline: Math.max(total - online, 0),
      blocked,
      withCoordinates,
      ...statusCounts,
      avgHumidity:
        humidities.length > 0 ? humidities.reduce((sum, value) => sum + value, 0) / humidities.length : null,
      avgTemperature:
        temperatures.length > 0 ? temperatures.reduce((sum, value) => sum + value, 0) / temperatures.length : null,
      lastUpdate
    };
  }, [equipmentCards]);

  const companyDistribution = useMemo(() => {
    const map = {};
    visibleCards.forEach(card => {
      const company = card.company || 'Sem empresa';
      if (!map[company]) {
        map[company] = { company, total: 0, versions: {} };
      }
      map[company].total += 1;
      const version = card.version || 'Sem versão';
      map[company].versions[version] = (map[company].versions[version] || 0) + 1;
    });

    return Object.values(map)
      .map(item => {
        const versionList = Object.entries(item.versions).sort((a, b) => b[1] - a[1]);
        return {
          ...item,
          versionList,
          versionSummary: versionList.map(([version, count]) => `${version} (${count})`).join(' · ')
        };
      })
      .sort((a, b) => b.total - a.total);
  }, [visibleCards]);

  const criticalCards = useMemo(() => {
    return visibleCards
      .filter(card => card.reading.key === 'danger' || card.reading.key === 'attention' || card.daysLeft === 0)
      .sort((a, b) => {
        const weight = { danger: 0, attention: 1, nodata: 2, ok: 3 };
        return weight[a.reading.key] - weight[b.reading.key];
      })
      .slice(0, 6);
  }, [visibleCards]);

  const paginatedCards = useMemo(() => {
    const start = currentPage * rowsPerPage;
    return visibleCards.slice(start, start + rowsPerPage);
  }, [visibleCards, currentPage, rowsPerPage]);

  const totalPages = Math.max(Math.ceil(visibleCards.length / rowsPerPage), 1);

  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm, statusFilter, periodFilter, rowsPerPage]);

  const statusChartData = useMemo(
    () => ({
      labels: [STATUS_META.ok.label, STATUS_META.attention.label, STATUS_META.danger.label, STATUS_META.nodata.label],
      datasets: [
        {
          data: [stats.ok, stats.attention, stats.danger, stats.nodata],
          backgroundColor: [
            STATUS_META.ok.color,
            STATUS_META.attention.color,
            STATUS_META.danger.color,
            STATUS_META.nodata.color
          ],
          borderColor: '#ffffff',
          borderWidth: 2,
          hoverOffset: 4
        }
      ]
    }),
    [stats]
  );

  const chartFont = "'Segoe UI', system-ui, -apple-system, sans-serif";

  const statusChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '68%',
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#252423',
        titleFont: { family: chartFont, size: 12 },
        bodyFont: { family: chartFont, size: 12 },
        borderColor: 'rgba(255,255,255,0.1)',
        borderWidth: 1,
        padding: 10,
        cornerRadius: 6
      }
    },
    onClick: (event, elements) => {
      if (!elements.length) return;
      const statusKeys = ['ok', 'attention', 'danger', 'nodata'];
      const key = statusKeys[elements[0].index];
      setChartModal({
        show: true,
        title: STATUS_META[key].label,
        items: equipmentCards.filter(card => card.reading.key === key)
      });
    }
  };

  const topCompaniesByVolume = useMemo(() => companyDistribution.slice(0, 8), [companyDistribution]);

  const companyVersionKeys = useMemo(() => {
    const versionTotals = {};
    topCompaniesByVolume.forEach(company => {
      Object.entries(company.versions).forEach(([version, count]) => {
        versionTotals[version] = (versionTotals[version] || 0) + count;
      });
    });

    const sortedVersions = Object.entries(versionTotals)
      .sort((a, b) => b[1] - a[1])
      .map(([version]) => version);

    const primary = sortedVersions.slice(0, 6);
    return sortedVersions.length > 6 ? [...primary, 'Outras'] : primary;
  }, [topCompaniesByVolume]);

  const primaryCompanyVersions = useMemo(
    () => companyVersionKeys.filter(version => version !== 'Outras'),
    [companyVersionKeys]
  );

  const companyChartData = useMemo(
    () => ({
      labels: topCompaniesByVolume.map(item => item.company),
      datasets: companyVersionKeys.map((versionKey, index) => ({
        label: versionKey,
        data: topCompaniesByVolume.map(company => {
          if (versionKey === 'Outras') {
            return Object.entries(company.versions).reduce((sum, [version, count]) => {
              if (primaryCompanyVersions.includes(version)) return sum;
              return sum + count;
            }, 0);
          }
          return company.versions[versionKey] || 0;
        }),
        backgroundColor: COMPANY_VERSION_COLORS[index % COMPANY_VERSION_COLORS.length],
        borderRadius: 4
      }))
    }),
    [topCompaniesByVolume, companyVersionKeys, primaryCompanyVersions]
  );

  const companyChartOptions = useMemo(
    () => ({
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            color: '#323130',
            boxWidth: 8,
            usePointStyle: true,
            padding: 14,
            font: { family: chartFont, size: 11 }
          }
        },
        tooltip: {
          backgroundColor: '#252423',
          titleFont: { family: chartFont, size: 12 },
          bodyFont: { family: chartFont, size: 12 },
          padding: 10,
          cornerRadius: 6,
          callbacks: {
            title: items => {
              const company = topCompaniesByVolume[items[0]?.dataIndex];
              return company ? `${company.company} — ${company.total} equipamentos` : '';
            },
            label: context => {
              const value = context.parsed.x ?? 0;
              if (!value) return null;
              return `${context.dataset.label}: ${value}`;
            },
            afterBody: items => {
              const company = topCompaniesByVolume[items[0]?.dataIndex];
              if (!company?.versionList?.length) return [];
              return ['Tipos:', ...company.versionList.map(([version, count]) => `  ${version}: ${count}`)];
            }
          }
        }
      },
      scales: {
        x: {
          stacked: true,
          beginAtZero: true,
          ticks: { color: '#605e5c', precision: 0, font: { family: chartFont, size: 11 } },
          grid: { color: '#edebe9' }
        },
        y: {
          stacked: true,
          ticks: {
            color: '#323130',
            font: { family: chartFont, size: 11, weight: '600' },
            callback(value, index) {
              const item = topCompaniesByVolume[index];
              if (!item) return '';
              const label = `${item.company} (${item.total})`;
              return label.length > 26 ? `${label.slice(0, 24)}…` : label;
            }
          },
          grid: { display: false }
        }
      },
      onClick: (event, elements) => {
        if (!elements.length) return;
        const companyItem = topCompaniesByVolume[elements[0].index];
        const versionKey = companyVersionKeys[elements[0].datasetIndex];
        if (!companyItem?.company) return;

        let items = visibleCards.filter(card => card.company === companyItem.company);
        if (versionKey === 'Outras') {
          items = items.filter(card => !primaryCompanyVersions.includes(card.version));
        } else if (versionKey) {
          items = items.filter(card => card.version === versionKey);
        }

        setChartModal({
          show: true,
          title: `${companyItem.company} — ${versionKey || 'todos'}`,
          items
        });
      }
    }),
    [topCompaniesByVolume, companyVersionKeys, primaryCompanyVersions, visibleCards]
  );

  const exportCSV = () => {
    if (visibleCards.length === 0) return;

    const headers = [
      'Serial',
      'Status',
      'Umidade',
      'Temperatura',
      'Cidade',
      'Estado',
      'Responsavel',
      'Telefone',
      'Plano',
      'Dias para ativacao',
      'Ultima leitura',
      'Bandeja',
      'Foto facial',
      'Rastreamento completo'
    ];

    const rows = visibleCards.map(card =>
      [
        card.serial,
        card.reading.label,
        card.measurement.humidity ?? '',
        card.measurement.temperature ?? '',
        card.city,
        card.state,
        card.responsible.name || card.rawEquipment.lastEmailActivation || '',
        card.responsible.fone || '',
        card.plan,
        card.daysLeft,
        card.lastUpdate ? moment(card.lastUpdate).format('DD/MM/YYYY HH:mm') : '',
        card.resources.tray ? 'Sim' : 'Nao',
        card.resources.face ? 'Sim' : 'Nao',
        card.resources.tracking ? 'Sim' : 'Nao'
      ]
        .map(value => `"${String(value).replace(/"/g, '""')}"`)
        .join(',')
    );

    const blob = new Blob(['\uFEFF' + [headers.join(','), ...rows].join('\n')], {
      type: 'text/csv;charset=utf-8;'
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Monitoramento_Equipamentos_${moment().format('DD-MM-YYYY_HH-mm')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const selectEquipment = useCallback((card, options = {}) => {
    setSelectedSerial(card.serial);
    if (options.openModal) {
      setSelectedDetail(card);
    }
    if (options.scrollToPanel) {
      requestAnimationFrame(() => {
        focusPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, []);

  const openDetail = useCallback(
    card => {
      selectEquipment(card, { openModal: true });
    },
    [selectEquipment]
  );

  const handleTableRowClick = useCallback(
    card => {
      if (tableClickTimeoutRef.current) clearTimeout(tableClickTimeoutRef.current);
      tableClickTimeoutRef.current = setTimeout(() => {
        selectEquipment(card);
        tableClickTimeoutRef.current = null;
      }, 220);
    },
    [selectEquipment]
  );

  const handleTableRowDoubleClick = useCallback(
    card => {
      if (tableClickTimeoutRef.current) {
        clearTimeout(tableClickTimeoutRef.current);
        tableClickTimeoutRef.current = null;
      }
      openDetail(card);
    },
    [openDetail]
  );

  const filteredModalItems = chartModal.items.filter(card => {
    const search = modalSearchTerm.trim().toUpperCase();
    if (!search) return true;
    return [card.serial, card.city, card.state, card.responsible.name, card.rawEquipment.lastEmailActivation]
      .filter(Boolean)
      .join(' ')
      .toUpperCase()
      .includes(search);
  });

  return (
    <Page title="Geral da empresa">
      <Headers page="Geral da empresa" />
      {loading && <Reload />}

      <div className="company-cockpit">
        <section className="pbi-report-header">
          <div>
            <h1>Monitoramento de Equipamentos</h1>
            <p className="pbi-report-subtitle">
              Painel executivo com indicadores, mapa de unidades, distribuicao por status e inventario detalhado.
            </p>
          </div>
          <div className="pbi-report-actions">
            <span className="pbi-report-timestamp">
              <FaClock />
              Atualizado {stats.lastUpdate ? stats.lastUpdate.format('DD/MM/YYYY HH:mm') : '--'}
            </span>
            <Button variant="success" size="sm" className="pbi-btn-refresh" onClick={fetchData}>
              <FaSatelliteDish className="me-1" />
              Atualizar
            </Button>
          </div>
        </section>

        <section className="pbi-slicer-bar">
          <div className="pbi-slicer-group">
            <span className="pbi-slicer-title">Status</span>
            <div className="pbi-slicer-pills">
              {statusFilters.map(option => (
                <button
                  key={option.key}
                  type="button"
                  className={`pbi-slicer-pill ${statusFilter === option.key ? 'active' : ''}`}
                  onClick={() => setStatusFilter(option.key)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          <div className="pbi-slicer-group">
            <span className="pbi-slicer-title">Período</span>
            <div className="pbi-slicer-pills">
              {periodOptions.map(option => (
                <button
                  key={option.key}
                  type="button"
                  className={`pbi-slicer-pill ${periodFilter === option.key ? 'active' : ''}`}
                  onClick={() => setPeriodFilter(option.key)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          <div className="pbi-slicer-meta">
            <strong>{formatNumber(visibleCards.length)}</strong> registros visíveis
          </div>
        </section>

        <Row className="g-3 mb-3 pbi-kpi-row">
          <Col xs={12} md={6} xl={3}>
            <MetricCard
              label="Equipamentos monitorados"
              value={stats.total}
              hint={`${stats.withCoordinates} com localizacao`}
              tone="brand"
              onClick={() => setChartModal({ show: true, title: 'Todos os equipamentos', items: equipmentCards })}
            />
          </Col>
          <Col xs={12} md={6} xl={3}>
            <MetricCard
              label="Dentro da faixa"
              value={stats.ok}
              hint={`${stats.total ? formatNumber((stats.ok / stats.total) * 100, 1) : '0,0'}% do total`}
              tone="success"
              progress={stats.total ? (stats.ok / stats.total) * 100 : 0}
              onClick={() =>
                setChartModal({
                  show: true,
                  title: STATUS_META.ok.label,
                  items: equipmentCards.filter(card => card.reading.key === 'ok')
                })
              }
            />
          </Col>
          <Col xs={12} md={6} xl={3}>
            <MetricCard
              label="Atenção"
              value={stats.attention}
              hint="Fora da faixa ideal"
              tone="warning"
              progress={stats.total ? (stats.attention / stats.total) * 100 : 0}
              onClick={() =>
                setChartModal({
                  show: true,
                  title: STATUS_META.attention.label,
                  items: equipmentCards.filter(card => card.reading.key === 'attention')
                })
              }
            />
          </Col>
          <Col xs={12} md={6} xl={3}>
            <MetricCard
              label="Fora da faixa"
              value={stats.danger}
              hint={`${stats.blocked} bloqueados`}
              tone="danger"
              progress={stats.total ? (stats.danger / stats.total) * 100 : 0}
              onClick={() =>
                setChartModal({
                  show: true,
                  title: STATUS_META.danger.label,
                  items: equipmentCards.filter(card => card.reading.key === 'danger')
                })
              }
            />
          </Col>
        </Row>

        <Row className="g-3 mb-3 map-focus-row">
          <Col xs={12} xl={8} className="map-panel-col">
            <Card className="cockpit-panel h-100 map-panel-card">
              <Card.Body className="map-panel-body">
                <div className="map-header pbi-panel-header">
                  <div>
                    <span className="panel-kicker">Visual</span>
                    <h2>Mapa de unidades</h2>
                  </div>
                  <div className="map-legend">
                    {Object.entries(STATUS_META).map(([key, meta]) => (
                      <span key={key}>
                        <i style={{ backgroundColor: meta.color }} />
                        {meta.label}
                      </span>
                    ))}
                  </div>
                </div>
                <EquipmentMap
                  cards={visibleCards}
                  selectedSerial={selectedCard?.serial}
                  onSelect={serial => {
                    const card = visibleCards.find(item => item.serial === serial);
                    if (card) selectEquipment(card);
                  }}
                />
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} xl={4} ref={focusPanelRef} className="equipment-focus-col">
            <Card className="cockpit-panel h-100 equipment-focus-panel-card">
              <Card.Body className="equipment-focus-panel-body">
                {selectedUnitContext ? (
                  <SelectedEquipmentPanel
                    unitContext={selectedUnitContext}
                    onOpenDetail={() => openDetail(selectedUnitContext.selectedCard)}
                  />
                ) : (
                  <EmptyState title="Nenhuma unidade selecionada" />
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-3 mb-3">
          <Col xs={12} md={6} xl={3}>
            <TelemetryCard
              icon={<FaTint />}
              label="Umidade media"
              value={`${formatNumber(stats.avgHumidity, 1)}%`}
              hint={`Faixa ideal ${HUMIDITY_MIN}% - ${HUMIDITY_MAX}%`}
              accent={BRAND_COLORS.primary}
            />
          </Col>
          <Col xs={12} md={6} xl={3}>
            <TelemetryCard
              icon={<FaThermometerHalf />}
              label="Temperatura media"
              value={`${formatNumber(stats.avgTemperature, 1)}°C`}
              hint={`Ideal ${TEMPERATURE_MIN}°C - ${TEMPERATURE_MAX}°C`}
              accent={BRAND_COLORS.danger}
            />
          </Col>
          <Col xs={12} md={6} xl={3}>
            <TelemetryCard
              icon={<FaSignal />}
              label="Conexao"
              value={`${stats.online}/${stats.total}`}
              hint={`${stats.offline} sem sinal online`}
              accent={BRAND_COLORS.warning}
            />
          </Col>
          <Col xs={12} md={6} xl={3}>
            <TelemetryCard
              icon={<FaShieldAlt />}
              label="Recursos ativos"
              value={equipmentCards.filter(card => card.resources.tray || card.resources.face || card.resources.tracking).length}
              hint="Bandeja, face ou rastreamento"
              accent={BRAND_COLORS.primaryDark}
            />
          </Col>
        </Row>

        <Row className="g-3 mb-3">
          <Col xs={12} xl={4}>
            <Card className="cockpit-panel h-100">
              <Card.Body>
                <div className="panel-title pbi-panel-header">
                  <span className="panel-kicker">Visual</span>
                  <h2>Distribuição por status</h2>
                </div>
                <div className="chart-wrap position-relative">
                  <Doughnut data={statusChartData} options={statusChartOptions} />
                  <div className="chart-center">
                    <strong>{stats.total}</strong>
                    <span>equipamentos</span>
                  </div>
                </div>
                <div className="status-grid">
                  {Object.entries(STATUS_META).map(([key, meta]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setStatusFilter(key)}
                      className={`status-chip ${statusFilter === key ? 'active' : ''}`}
                      style={{ borderColor: meta.border, backgroundColor: meta.bg }}
                    >
                      <span style={{ color: meta.color }}>{stats[key]}</span>
                      {meta.label}
                    </button>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} xl={5}>
            <Card className="cockpit-panel h-100">
              <Card.Body>
                <div className="panel-title pbi-panel-header">
                  <div>
                    <span className="panel-kicker">Visual</span>
                    <h2>Equipamentos por empresa</h2>
                    <p className="company-chart-caption">
                      Total de equipamentos por empresa, empilhado por tipo (prefixo do serial)
                    </p>
                  </div>
                </div>
                <div className="company-chart-wrap">
                  {companyDistribution.length > 0 ? (
                    <Bar data={companyChartData} options={companyChartOptions} />
                  ) : (
                    <EmptyState title="Sem dados para o gráfico" />
                  )}
                </div>
                {topCompaniesByVolume.length > 0 && (
                  <ul className="company-version-legend">
                    {topCompaniesByVolume.map(company => (
                      <li key={company.company}>
                        <strong title={company.company}>
                          {company.company} <span>({company.total})</span>
                        </strong>
                        <span className="company-version-legend-text" title={company.versionSummary}>
                          {company.versionSummary}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} xl={3}>
            <Card className="cockpit-panel h-100">
              <Card.Body>
                <div className="panel-title pbi-panel-header">
                  <span className="panel-kicker">Visual</span>
                  <h2>Alertas prioritarios</h2>
                </div>
                <div className="priority-list">
                  {criticalCards.length > 0 ? (
                    criticalCards.map(card => (
                      <div key={card.serial} className="priority-item">
                        <span className="priority-dot" style={{ backgroundColor: card.reading.color }} />
                        <div>
                          <strong>{card.serial}</strong>
                          <small>
                            {card.city}, {card.state}
                          </small>
                        </div>
                        <Badge style={{ backgroundColor: card.reading.color }}>{card.reading.short}</Badge>
                      </div>
                    ))
                  ) : (
                    <EmptyState title="Nenhum alerta critico" />
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="cockpit-panel">
          <Card.Body>
            <div className="table-toolbar pbi-panel-header">
              <div>
                <span className="panel-kicker">Tabela</span>
                <h2>Detalhamento de equipamentos</h2>
                <p>Clique na linha para selecionar · duplo clique para abrir detalhes completos.</p>
              </div>
              <div className="toolbar-controls">
                <div className="search-control">
                  <FaSearch />
                  <input
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Buscar serie, cidade, responsavel..."
                  />
                  {searchTerm && (
                    <button type="button" onClick={() => setSearchTerm('')}>
                      <FaTimes />
                    </button>
                  )}
                </div>
                <Button className="pbi-btn-export" onClick={exportCSV} disabled={visibleCards.length === 0}>
                  <FaDownload className="me-2" />
                  CSV
                </Button>
              </div>
            </div>

            {selectedCard && (
              <TableSelectionBar
                card={selectedCard}
                onOpenDetail={() => openDetail(selectedCard)}
                onScrollToPanel={() => selectEquipment(selectedCard, { scrollToPanel: true })}
              />
            )}

            <div className="equipment-table-wrap">
              <table className="equipment-table">
                <thead>
                  <tr>
                    <th>Equipamento</th>
                    <th>Status</th>
                    <th>Localizacao</th>
                    <th>Responsavel</th>
                    <th>Umidade</th>
                    <th>Temperatura</th>
                    <th>Ultima leitura</th>
                    <th>Recursos</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedCards.length > 0 ? (
                    paginatedCards.map(card => (
                      <tr
                        key={card.serial}
                        className={selectedSerial === card.serial ? 'is-selected' : ''}
                        onClick={() => handleTableRowClick(card)}
                        onDoubleClick={() => handleTableRowDoubleClick(card)}
                      >
                        <td>
                          <strong>{card.serial}</strong>
                          <small>{card.plan}</small>
                        </td>
                        <td className="status-cell">
                          <StatusBadge reading={card.reading} />
                        </td>
                        <td>
                          {card.city}, {card.state}
                          <small>{card.hasCoordinates ? `${card.lat}, ${card.lng}` : 'Sem coordenadas'}</small>
                        </td>
                        <td>
                          {card.responsible.name || card.rawEquipment.lastEmailActivation || 'N/A'}
                          <small>{card.responsible.fone || card.responsible.cpfcnpj || ''}</small>
                        </td>
                        <td>{card.measurement.humidity !== null ? `${formatNumber(card.measurement.humidity, 1)}%` : '--'}</td>
                        <td>
                          {card.measurement.temperature !== null
                            ? `${formatNumber(card.measurement.temperature, 1)}°C`
                            : '--'}
                        </td>
                        <td>{formatDuration(card.lastUpdate)}</td>
                        <td>
                          <ResourceDots resources={card.resources} />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8">
                        <EmptyState title="Nenhum equipamento encontrado" />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="table-pagination">
              <div>
                <span>Linhas</span>
                <Form.Select value={rowsPerPage} onChange={(event) => setRowsPerPage(Number(event.target.value))}>
                  {[10, 20, 40, 100].map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <span>
                Pagina {currentPage + 1} de {totalPages}
              </span>
              <div>
                <Button
                  variant="outline-success"
                  size="sm"
                  disabled={currentPage === 0}
                  onClick={() => setCurrentPage(page => Math.max(page - 1, 0))}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline-success"
                  size="sm"
                  disabled={currentPage + 1 >= totalPages}
                  onClick={() => setCurrentPage(page => Math.min(page + 1, totalPages - 1))}
                >
                  Proxima
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>

      <EquipmentDetailModal
        card={selectedDetail}
        show={!!selectedDetail}
        onHide={() => setSelectedDetail(null)}
      />

      <Modal
        show={chartModal.show}
        onHide={() => {
          setChartModal({ show: false, title: '', items: [] });
          setModalSearchTerm('');
        }}
        size="lg"
        centered
        contentClassName="cockpit-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>{chartModal.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="search-control modal-search">
            <FaSearch />
            <input
              value={modalSearchTerm}
              onChange={(event) => setModalSearchTerm(event.target.value)}
              placeholder="Filtrar nesta lista..."
            />
          </div>
          <ListGroup className="modal-equipment-list">
            {filteredModalItems.length > 0 ? (
              filteredModalItems.map(card => (
                <ListGroup.Item
                  key={card.serial}
                  action
                  onClick={() => {
                    setChartModal({ show: false, title: '', items: [] });
                    setModalSearchTerm('');
                    openDetail(card);
                  }}
                >
                  <div>
                    <strong>{card.serial}</strong>
                    <small>
                      {card.city}, {card.state}
                    </small>
                  </div>
                  <Badge style={{ backgroundColor: card.reading.color }}>{card.reading.short}</Badge>
                </ListGroup.Item>
              ))
            ) : (
              <EmptyState title="Nenhum equipamento nesta lista" />
            )}
          </ListGroup>
        </Modal.Body>
      </Modal>

      <style>{cockpitStyles}</style>
    </Page>
  );
};

const MetricCard = ({ label, value, hint, tone, onClick, progress }) => (
  <button type="button" className={`pbi-kpi pbi-kpi-${tone}`} onClick={onClick}>
    <span className="pbi-kpi-label">{label}</span>
    <strong className="pbi-kpi-value">{formatNumber(value)}</strong>
    {hint && <small className="pbi-kpi-hint">{hint}</small>}
    {progress !== undefined && progress !== null && (
      <div className="pbi-kpi-progress" aria-hidden="true">
        <div className="pbi-kpi-progress-fill" style={{ width: `${Math.min(100, Math.max(0, progress))}%` }} />
      </div>
    )}
  </button>
);

const TelemetryCard = ({ icon, label, value, hint, accent }) => (
  <Card className="pbi-stat-card h-100" style={{ '--accent': accent }}>
    <Card.Body className="pbi-stat-card-body">
      <span className="pbi-stat-icon">{icon}</span>
      <div>
        <small className="pbi-stat-label">{label}</small>
        <strong className="pbi-stat-value">{value}</strong>
        <span className="pbi-stat-hint">{hint}</span>
      </div>
    </Card.Body>
  </Card>
);

const EquipmentMap = ({ cards, selectedSerial, onSelect }) => {
  const mapRef = useRef(null);
  const [hoveredSerial, setHoveredSerial] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [mapBounds, setMapBounds] = useState(null);
  const [mapLoading, setMapLoading] = useState(() => sessionStorage.getItem('mapLoaded') !== 'true');
  const unitCards = useMemo(() => {
    const storedUnits = getStoredUnits();
    const cardsBySerial = cards.reduce((acc, card) => {
      if (card.serial) acc[card.serial] = card;
      return acc;
    }, {});

    return storedUnits
      .map((unit, index) => {
        const unitSerials = Array.isArray(unit?.equipments) ? unit.equipments.filter(Boolean) : [];
        const linkedCards = unitSerials.map(serial => cardsBySerial[serial]).filter(Boolean);
        const coordinateCards = linkedCards.filter(card => card.hasCoordinates);
        const fallbackLat =
          coordinateCards.length > 0
            ? coordinateCards.reduce((sum, card) => sum + card.lat, 0) / coordinateCards.length
            : null;
        const fallbackLng =
          coordinateCards.length > 0
            ? coordinateCards.reduce((sum, card) => sum + card.lng, 0) / coordinateCards.length
            : null;
        const localPoint = Array.isArray(unit?.localEquipament)
          ? unit.localEquipament.find(item => {
              const lat = getNumericValue(item?.lat, item?.latitude);
              const lng = getNumericValue(item?.lng, item?.longitude);
              return lat !== null && lng !== null && lat !== 0 && lng !== 0;
            })
          : null;

        const lat = getNumericValue(
          unit?.lat,
          unit?.latitude,
          unit?.address?.lat,
          unit?.address?.latitude,
          unit?.address?.location?.lat,
          unit?.location?.lat,
          unit?.coordinates?.lat,
          unit?.coordinates?.[1],
          unit?.address?.coordinates?.[1],
          localPoint?.lat,
          localPoint?.latitude,
          fallbackLat
        );
        const lng = getNumericValue(
          unit?.lng,
          unit?.lon,
          unit?.longitude,
          unit?.address?.lng,
          unit?.address?.lon,
          unit?.address?.longitude,
          unit?.address?.location?.lng,
          unit?.location?.lng,
          unit?.coordinates?.lng,
          unit?.coordinates?.[0],
          unit?.address?.coordinates?.[0],
          localPoint?.lng,
          localPoint?.longitude,
          fallbackLng
        );

        const selectedCard = linkedCards.find(card => card.serial === selectedSerial) || linkedCards[0] || coordinateCards[0];
        const state = unit?.address?.state?.shortname || unit?.state?.shortname || unit?.state || selectedCard?.state || 'N/A';
        const city =
          unit?.address?.city?.longname ||
          unit?.address?.city?.shortname ||
          unit?.city?.longname ||
          unit?.city?.shortname ||
          unit?.city ||
          selectedCard?.city ||
          'Local nao informado';

        return {
          id: unit?._id || unit?.id || unit?.name || `unit-${index}`,
          serial: selectedCard?.serial || unitSerials[0] || unit?._id || `unit-${index}`,
          name: unit?.name || unit?.companyName || `Unidade ${index + 1}`,
          city,
          state,
          lat,
          lng,
          totalEquipments: unitSerials.length,
          linkedCards,
          reading: selectedCard?.reading || STATUS_META.nodata,
          hasCoordinates: lat !== null && lng !== null && lat !== 0 && lng !== 0
        };
      })
      .filter(unit => unit.hasCoordinates && Number.isFinite(unit.lat) && Number.isFinite(unit.lng));
  }, [cards, selectedSerial]);
  const positionedCards = unitCards;
  const MAX_VISIBLE_POINTS = 450;

  const updateBoundsState = useCallback((mapInstance) => {
    if (!mapInstance) return;
    const bounds = mapInstance.getBounds();
    if (!bounds) return;
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    setMapBounds({
      north: ne.lat(),
      east: ne.lng(),
      south: sw.lat(),
      west: sw.lng()
    });
  }, []);

  const fitBoundsToMarkers = useCallback(() => {
    if (!mapRef.current || positionedCards.length === 0 || !window.google?.maps) return;

    const bounds = new window.google.maps.LatLngBounds();
    positionedCards.forEach(card => {
      bounds.extend(new window.google.maps.LatLng(card.lat, card.lng));
    });

    if (positionedCards.length === 1) {
      const [card] = positionedCards;
      mapRef.current.panTo({ lat: card.lat, lng: card.lng });
      setTimeout(() => {
        mapRef.current?.setZoom(15);
      }, 300);
      return;
    }

    try {
      mapRef.current.fitBounds(bounds, {
        padding: { top: 30, right: 30, bottom: 30, left: 30 },
        duration: 800
      });
    } catch {
      mapRef.current.fitBounds(bounds);
    }
  }, [positionedCards]);

  useEffect(() => {
    if (positionedCards.length === 0) {
      setMapLoading(false);
      return;
    }

    if (mapRef.current && mapReady) {
      const timer = setTimeout(() => {
        fitBoundsToMarkers();
        updateBoundsState(mapRef.current);
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [fitBoundsToMarkers, mapReady, positionedCards.length, updateBoundsState]);

  useEffect(() => {
    const wasLoaded = sessionStorage.getItem('mapLoaded') === 'true';
    if (wasLoaded && mapRef.current) {
      const timer = setTimeout(() => setMapLoading(false), 100);
      return () => clearTimeout(timer);
    }
  }, []);

  const visibleCards = useMemo(() => {
    if (!positionedCards.length) return [];
    if (!mapReady || !mapBounds) return positionedCards.slice(0, MAX_VISIBLE_POINTS);

    const latMargin = (mapBounds.north - mapBounds.south) * 0.25;
    const lngMargin = (mapBounds.east - mapBounds.west) * 0.25;
    const filtered = positionedCards.filter(card => (
      card.lat >= mapBounds.south - latMargin &&
      card.lat <= mapBounds.north + latMargin &&
      card.lng >= mapBounds.west - lngMargin &&
      card.lng <= mapBounds.east + lngMargin
    ));

    return (filtered.length > 0 ? filtered : positionedCards).slice(0, MAX_VISIBLE_POINTS);
  }, [mapBounds, mapReady, positionedCards]);

  const wasMapLoaded = sessionStorage.getItem('mapLoaded') === 'true';

  return (
    <div className="operation-map">
      <MapLoader isLoading={mapLoading} />

      <div className={`operation-map-content ${!mapLoading ? 'loaded' : ''} ${wasMapLoaded ? 'fast-load' : ''}`}>
        <GoogleMapReact
          bootstrapURLKeys={{ key: process.env.REACT_APP_GOOGLE_API_KEY }}
          defaultCenter={{ lat: -14.2908, lng: -55.9073 }}
          defaultZoom={4.5}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={({ map }) => {
            mapRef.current = map;
            setMapReady(true);
            updateBoundsState(map);

            if (positionedCards.length > 0) {
              fitBoundsToMarkers();
            }

            map.addListener('idle', () => updateBoundsState(map));

            window.google.maps.event.addListenerOnce(map, 'tilesloaded', () => {
              const wasLoaded = sessionStorage.getItem('mapLoaded') === 'true';
              const delay = wasLoaded ? 0 : 400;
              setTimeout(() => {
                setMapLoading(false);
                sessionStorage.setItem('mapLoaded', 'true');
              }, delay);
            });
          }}
        >
          {visibleCards.map(card => {
            const isSelected =
              selectedSerial && card.linkedCards.some(linked => linked.serial === selectedSerial);

            return (
              <MapMarker
                key={card.id}
                lat={card.lat}
                lng={card.lng}
                onMouseEnter={() => setHoveredSerial(card.id)}
                onMouseLeave={() => setHoveredSerial(null)}
              >
                <div className={`company-map-marker ${isSelected ? 'selected' : ''}`}>
                  <button
                    type="button"
                    className="company-map-marker-button"
                    onClick={() => onSelect(card.serial)}
                    aria-label={`Unidade ${card.name}`}
                  >
                    <img
                      alt={`Unidade ${card.name}`}
                      src="/images/ic_silo.png"
                      className="company-map-marker-img"
                    />
                  </button>
                </div>
                {(hoveredSerial === card.id || isSelected) && (
                  <div className="company-map-tooltip">
                    <strong>{card.name}</strong>
                  </div>
                )}
              </MapMarker>
            );
          })}
        </GoogleMapReact>
      </div>

      {positionedCards.length === 0 && (
        <div className="map-empty">
          <FaMapMarkerAlt />
          <span>Nenhuma unidade com coordenadas no localStorage</span>
        </div>
      )}
      <div className="map-caption">
        <FaMapMarkedAlt />
        <span>{positionedCards.length} unidades georreferenciadas</span>
      </div>
    </div>
  );
};

const SelectedEquipmentPanel = ({ unitContext, onOpenDetail }) => {
  const { unitName, city, state, selectedCard, linkedCards, reading, avgHumidity, avgTemperature, humiditySamples, temperatureSamples, equipmentCount, onlineCount } =
    unitContext;

  const samplesLabel =
    humiditySamples > 0 || temperatureSamples > 0
      ? `Média das últimas leituras de ${Math.max(humiditySamples, temperatureSamples)} equipamento(s)`
      : 'Sem leituras recentes nos equipamentos';

  return (
    <div className="selected-panel">
      <div className="selected-top">
        <div className="selected-avatar">{getInitials(unitName)}</div>
        <div>
          <span className="panel-kicker">Unidade em foco</span>
          <h2>{unitName}</h2>
          <p>
            {city}, {state}
          </p>
          <small className="selected-unit-meta">
            {equipmentCount} equipamento{equipmentCount === 1 ? '' : 's'} · {onlineCount} online
            {selectedCard?.serial ? ` · destaque: ${selectedCard.serial}` : ''}
          </small>
        </div>
      </div>

      <div className="status-callout" style={{ borderColor: reading.border, backgroundColor: reading.bg }}>
        <span style={{ color: reading.color }}>{reading.label}</span>
        <strong>
          {avgHumidity !== null ? `${formatNumber(avgHumidity, 1)}% Umidade média` : 'Sem média de umidade'}
        </strong>
        <small>{samplesLabel}</small>
      </div>

      <div className="dial-row">
        <MiniDial label="Umidade média" value={avgHumidity} suffix="%" min={0} max={100} color={BRAND_COLORS.primary} />
        <MiniDial label="Temp. média" value={avgTemperature} suffix="°C" min={0} max={50} color={BRAND_COLORS.danger} />
      </div>

      <ListGroup className="selected-list">
        <DetailItem
          icon={<FaTint />}
          label="Umidade média"
          value={avgHumidity !== null ? `${formatNumber(avgHumidity, 1)}% (${humiditySamples}/${equipmentCount})` : 'N/A'}
        />
        <DetailItem
          icon={<FaThermometerHalf />}
          label="Temperatura média"
          value={avgTemperature !== null ? `${formatNumber(avgTemperature, 1)}°C (${temperatureSamples}/${equipmentCount})` : 'N/A'}
        />
        <DetailItem icon={<FaMicrochip />} label="Equipamentos" value={`${equipmentCount} vinculados`} />
        <DetailItem icon={<FaSignal />} label="Conexão" value={`${onlineCount}/${equipmentCount} online`} />
        {linkedCards.length > 1 && (
          <DetailItem
            icon={<FaHistory />}
            label="Faixas ideais"
            value={`Umidade ${HUMIDITY_MIN}-${HUMIDITY_MAX}% · Temperatura ${TEMPERATURE_MIN}-${TEMPERATURE_MAX}°C`}
          />
        )}
      </ListGroup>

      <Button variant="outline-success" size="sm" className="w-100 mt-3" onClick={onOpenDetail}>
        Ver equipamento em destaque
      </Button>
    </div>
  );
};

const MiniDial = ({ label, value, suffix, min, max, color }) => {
  const safeValue = value === null ? min : value;
  const progress = Math.max(0, Math.min(100, ((safeValue - min) / (max - min)) * 100));

  return (
    <div className="mini-dial" style={{ '--progress': `${progress * 3.6}deg`, '--dial-color': color }}>
      <div>
        <strong>{value !== null ? formatNumber(value, 1) : '--'}</strong>
        <span>{suffix}</span>
      </div>
      <small>{label}</small>
    </div>
  );
};

const TableSelectionBar = ({ card, onOpenDetail, onScrollToPanel }) => (
  <div className="table-selection-bar">
    <div className="table-selection-bar-main">
      <div className="table-selection-bar-id">
        <span className="table-selection-bar-kicker">Equipamento selecionado</span>
        <strong>{card.serial}</strong>
        <small>
          {card.city}, {card.state} · {card.plan}
        </small>
      </div>
      <StatusBadge reading={card.reading} />
      <div className="table-selection-bar-metrics">
        <span>
          <FaTint className="me-1" />
          {card.measurement.humidity !== null ? `${formatNumber(card.measurement.humidity, 1)}%` : '--'}
        </span>
        <span>
          <FaThermometerHalf className="me-1" />
          {card.measurement.temperature !== null ? `${formatNumber(card.measurement.temperature, 1)}°C` : '--'}
        </span>
        <span>
          <FaSignal className="me-1" />
          {card.isOnline ? 'Online' : 'Offline'}
        </span>
        <span>
          <FaClock className="me-1" />
          {formatDuration(card.lastUpdate)}
        </span>
      </div>
    </div>
    <div className="table-selection-bar-actions">
      <Button variant="outline-success" size="sm" onClick={onScrollToPanel}>
        <FaMapMarkedAlt className="me-1" />
        Ver no mapa
      </Button>
      <Button variant="success" size="sm" onClick={onOpenDetail}>
        Detalhes completos
      </Button>
    </div>
  </div>
);

const StatusBadge = ({ reading }) => (
  <span className={`status-pill status-pill--${reading.key}`} title={reading.label}>
    <span className="status-pill-dot" aria-hidden="true" />
    <span className="status-pill-text">{reading.short}</span>
  </span>
);

const ResourceDots = ({ resources }) => (
  <div className="resource-dots">
    <span className={resources.tray ? 'active' : ''} title="Bandeja">
      <FaBoxOpen />
    </span>
    <span className={resources.face ? 'active' : ''} title="Foto facial">
      <FaCamera />
    </span>
    <span className={resources.tracking ? 'active' : ''} title="Rastreamento">
      <FaMapMarkedAlt />
    </span>
  </div>
);

const EquipmentDetailModal = ({ card, show, onHide }) => (
  <Modal show={show} onHide={onHide} size="xl" centered contentClassName="cockpit-modal">
    <Modal.Header closeButton>
      <Modal.Title>
        <FaMicrochip className="me-2" />
        {card?.serial || 'Equipamento'}
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {card && (
        <Row className="g-3">
          <Col md={4}>
            <Card className="modal-dark-card h-100">
              <Card.Body>
                <div className="status-callout mb-3" style={{ borderColor: card.reading.border, backgroundColor: card.reading.bg }}>
                  <span style={{ color: card.reading.color }}>{card.reading.label}</span>
                  <strong>{card.reading.short}</strong>
                  <small>Faixa ideal: {HUMIDITY_MIN}% - {HUMIDITY_MAX}% de umidade</small>
                </div>
                <ListGroup className="selected-list">
                  <DetailItem icon={<FaTint />} label="Umidade" value={card.measurement.humidity !== null ? `${formatNumber(card.measurement.humidity, 1)}%` : 'N/A'} />
                  <DetailItem icon={<FaThermometerHalf />} label="Temperatura" value={card.measurement.temperature !== null ? `${formatNumber(card.measurement.temperature, 1)}°C` : 'N/A'} />
                  <DetailItem icon={<FaClock />} label="Ultima leitura" value={formatDuration(card.lastUpdate)} />
                  <DetailItem icon={<FaTools />} label="Modulo" value={card.rawEquipment.equipid || 'N/A'} />
                  <DetailItem icon={<FaMicrochip />} label="Software" value={card.rawEquipment.versaosw || 'N/A'} />
                  <DetailItem icon={<FaMicrochip />} label="Firmware principal" value={card.rawEquipment.versaofwpri || 'N/A'} />
                  <DetailItem icon={<FaMicrochip />} label="Firmware oscilador" value={card.rawEquipment.versaofwosc || 'N/A'} />
                </ListGroup>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="modal-dark-card h-100">
              <Card.Body>
                <h5>Responsavel e recursos</h5>
                <ListGroup className="selected-list">
                  <DetailItem icon={<FaUserCircle />} label="Nome" value={card.responsible.name || 'N/A'} />
                  <DetailItem icon={<FaFileContract />} label="CPF/CNPJ" value={card.responsible.cpfcnpj || 'N/A'} />
                  <DetailItem icon={<FaSignal />} label="Telefone" value={card.responsible.fone || 'N/A'} />
                  <DetailItem icon={<FaUserCircle />} label="E-mail" value={card.rawEquipment.lastEmailActivation || 'N/A'} />
                  <DetailItem icon={<FaBoxOpen />} label="Bandeja" value={card.resources.tray ? 'Ativa' : 'Inativa'} />
                  <DetailItem icon={<FaCamera />} label="Foto facial" value={card.resources.face ? 'Ativa' : 'Inativa'} />
                  <DetailItem icon={<FaMapMarkedAlt />} label="Rastreamento" value={card.resources.tracking ? 'Ativo' : 'Inativo'} />
                </ListGroup>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="modal-dark-card h-100">
              <Card.Body>
                <h5>Localizacao</h5>
                <p className="text-muted">
                  {card.rawEquipment.address?.formatedAddr || `${card.city}, ${card.state}`}
                </p>
                {card.hasCoordinates ? (
                  <iframe
                    width="100%"
                    height="300"
                    frameBorder="0"
                    style={{ border: 0, borderRadius: 10 }}
                    src={`https://maps.google.com/maps?q=${card.lat},${card.lng}&z=13&t=k&output=embed`}
                    allowFullScreen
                    title="Localizacao do equipamento"
                  />
                ) : (
                  <div className="map-empty in-modal">
                    <FaMapMarkerAlt />
                    <span>Coordenadas GPS nao informadas</span>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </Modal.Body>
  </Modal>
);

const DetailItem = ({ icon, label, value }) => (
  <ListGroup.Item>
    <span>
      {icon}
      {label}
    </span>
    <strong>{value || 'N/A'}</strong>
  </ListGroup.Item>
);

const EmptyState = ({ title }) => (
  <div className="empty-state">
    <FaExclamationTriangle />
    <span>{title}</span>
  </div>
);

const cockpitStyles = `
  .company-cockpit,
  .cockpit-modal {
    --sgu-primary-dark: #1c4220;
    --sgu-primary: #8fad15;
    --sgu-graphite: #3c4042;
    --sgu-text: #323130;
    --sgu-text-muted: #605e5c;
    --sgu-border: #e1dfdd;
    --pbi-shadow: 0 1.6px 3.6px rgba(0, 0, 0, 0.08), 0 0.3px 0.9px rgba(0, 0, 0, 0.06);
    --pbi-radius: 10px;
    --pbi-radius-sm: 6px;
    font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
  }

  .company-cockpit {
    background: #f0f2f5;
    color: var(--sgu-text);
    min-height: 100vh;
    padding: 1rem !important;
    border-radius: 16px;
  }

  .company-cockpit .btn,
  .cockpit-modal .btn {
    border-radius: var(--pbi-radius-sm);
  }

  .pbi-report-header,
  .table-toolbar,
  .map-header,
  .panel-title {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
  }

  .pbi-report-header {
    background: #fff;
    border: 1px solid var(--sgu-border);
    border-radius: var(--pbi-radius);
    box-shadow: var(--pbi-shadow);
    margin-bottom: 12px;
    padding: 16px 20px;
  }

  .pbi-report-header h1 {
    color: var(--sgu-text);
    font-size: 1.35rem;
    font-weight: 600;
    margin: 0;
  }

  .pbi-report-subtitle,
  .table-toolbar p,
  .selected-top p {
    color: var(--sgu-text-muted);
    font-size: 0.875rem;
    margin: 6px 0 0;
    max-width: 720px;
  }

  .pbi-report-actions,
  .toolbar-controls {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    justify-content: flex-end;
  }

  .pbi-report-timestamp {
    align-items: center;
    color: var(--sgu-text-muted);
    display: inline-flex;
    font-size: 0.8125rem;
    gap: 6px;
  }

  .pbi-btn-refresh,
  .pbi-btn-export {
    background: #fff;
    border: 1px solid var(--sgu-border);
    border-radius: var(--pbi-radius-sm);
    color: var(--sgu-text);
    font-size: 0.8125rem;
    font-weight: 600;
    padding: 6px 14px;
  }

  .pbi-btn-export:hover:not(:disabled) {
    background: #f3f2f1;
    border-color: #c8c6c4;
    color: var(--sgu-text);
  }

  .pbi-slicer-bar {
    align-items: center;
    background: #fff;
    border: 1px solid var(--sgu-border);
    border-radius: var(--pbi-radius);
    box-shadow: var(--pbi-shadow);
    display: flex;
    flex-wrap: wrap;
    gap: 16px 24px;
    margin-bottom: 12px;
    padding: 12px 16px;
  }

  .pbi-slicer-group {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .pbi-slicer-title {
    color: var(--sgu-text-muted);
    font-size: 0.75rem;
    font-weight: 600;
    min-width: 52px;
    text-transform: uppercase;
  }

  .pbi-slicer-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .pbi-slicer-pill {
    background: #f3f2f1;
    border: 1px solid var(--sgu-border);
    border-radius: var(--pbi-radius-sm);
    color: var(--sgu-text);
    cursor: pointer;
    font-size: 0.8125rem;
    font-weight: 500;
    padding: 5px 12px;
    transition: background 0.15s ease, border-color 0.15s ease;
  }

  .pbi-slicer-pill:hover {
    background: #edebe9;
  }

  .pbi-slicer-pill.active {
    background: var(--sgu-primary-dark);
    border-color: var(--sgu-primary-dark);
    color: #fff;
  }

  .pbi-slicer-meta {
    color: var(--sgu-text-muted);
    font-size: 0.8125rem;
    margin-left: auto;
  }

  .pbi-slicer-meta strong {
    color: var(--sgu-text);
    font-weight: 600;
  }

  .pbi-kpi {
    background: #fff;
    border: 1px solid var(--sgu-border);
    border-radius: var(--pbi-radius);
    border-top: 3px solid var(--sgu-primary-dark);
    box-shadow: var(--pbi-shadow);
    color: var(--sgu-text);
    display: flex;
    flex-direction: column;
    min-height: 108px;
    padding: 14px 16px 12px;
    text-align: left;
    transition: box-shadow 0.15s ease;
    width: 100%;
  }

  .pbi-kpi:hover {
    box-shadow: 0 3.2px 7.2px rgba(0, 0, 0, 0.12);
  }

  .pbi-kpi-brand { border-top-color: var(--sgu-primary-dark); }
  .pbi-kpi-success { border-top-color: #28a745; }
  .pbi-kpi-warning { border-top-color: #e6a800; }
  .pbi-kpi-danger { border-top-color: #ff8c00; }

  .pbi-kpi-label {
    color: var(--sgu-text-muted);
    font-size: 0.8125rem;
    font-weight: 500;
  }

  .pbi-kpi-value {
    display: block;
    font-size: 1.75rem;
    font-weight: 600;
    line-height: 1.15;
    margin: 4px 0;
  }

  .pbi-kpi-hint {
    color: var(--sgu-text-muted);
    font-size: 0.75rem;
  }

  .pbi-kpi-progress {
    background: #edebe9;
    border-radius: var(--pbi-radius-sm);
    height: 4px;
    margin-top: 10px;
    overflow: hidden;
    width: 100%;
  }

  .pbi-kpi-progress-fill {
    background: currentColor;
    border-radius: inherit;
    height: 100%;
    transition: width 0.3s ease;
  }

  .pbi-kpi-success .pbi-kpi-progress-fill { background: #28a745; }
  .pbi-kpi-warning .pbi-kpi-progress-fill { background: #e6a800; }
  .pbi-kpi-danger .pbi-kpi-progress-fill { background: #ff8c00; }

  .panel-kicker {
    color: var(--sgu-text-muted);
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    margin-bottom: 4px;
    text-transform: uppercase;
  }

  .pbi-panel-header {
    border-bottom: 1px solid #edebe9;
    margin-bottom: 16px;
    padding-bottom: 12px;
    width: 100%;
  }

  .panel-title h2,
  .map-header h2,
  .table-toolbar h2,
  .selected-top h2 {
    color: var(--sgu-text);
    font-size: 1rem;
    font-weight: 600;
    margin: 0;
  }

  .cockpit-panel,
  .pbi-stat-card {
    background: #fff;
    border: 1px solid var(--sgu-border);
    border-radius: var(--pbi-radius);
    box-shadow: var(--pbi-shadow);
    color: var(--sgu-text);
    overflow: hidden;
  }

  .cockpit-panel > .card-body {
    padding: 16px;
  }

  .pbi-stat-card-body {
    align-items: center;
    display: flex;
    gap: 14px;
    padding: 16px;
  }

  .pbi-stat-icon {
    align-items: center;
    background: color-mix(in srgb, var(--accent), transparent 90%);
    border-left: 3px solid var(--accent);
    border-radius: var(--pbi-radius-sm);
    color: var(--accent);
    display: inline-flex;
    font-size: 1.1rem;
    height: 48px;
    justify-content: center;
    min-width: 48px;
    width: 48px;
  }

  .pbi-stat-label {
    color: var(--sgu-text-muted);
    display: block;
    font-size: 0.8125rem;
  }

  .pbi-stat-value {
    color: var(--sgu-text);
    display: block;
    font-size: 1.35rem;
    font-weight: 600;
    line-height: 1.2;
  }

  .pbi-stat-hint {
    color: var(--sgu-text-muted);
    display: block;
    font-size: 0.75rem;
  }

  .map-header {
    padding: 0;
  }

  .cockpit-panel .map-header.pbi-panel-header {
    margin: 0 0 12px;
    padding: 0 0 12px;
  }

  .map-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    justify-content: flex-end;
  }

  .map-legend span {
    align-items: center;
    color: var(--sgu-text-muted);
    display: inline-flex;
    font-size: 0.875rem;
    gap: 6px;
  }

  .map-legend i {
    border-radius: 999px;
    display: inline-block;
    height: 9px;
    width: 9px;
  }

  .map-focus-row {
    align-items: stretch;
  }

  .map-focus-row .map-panel-col,
  .map-focus-row .equipment-focus-col {
    display: flex;
    flex-direction: column;
  }

  .map-focus-row .map-panel-card,
  .map-focus-row .equipment-focus-panel-card {
    display: flex;
    flex: 1;
    flex-direction: column;
    width: 100%;
  }

  .map-focus-row .map-panel-body,
  .map-focus-row .equipment-focus-panel-body {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
  }

  .map-focus-row .operation-map {
    flex: 1;
    height: auto;
    min-height: 300px;
  }

  .map-focus-row .selected-panel {
    flex: 1;
  }

  .operation-map {
    background: #faf9f8;
    border: 1px solid var(--sgu-border);
    border-radius: var(--pbi-radius);
    height: 480px;
    margin: 0;
    overflow: hidden;
    position: relative;
  }

  .operation-map-content {
    height: 100%;
    opacity: 0;
    pointer-events: none;
    transform: scale(0.98);
    transition: opacity 1.2s cubic-bezier(0.4, 0, 0.2, 1) 0.2s,
                transform 1.2s cubic-bezier(0.4, 0, 0.2, 1) 0.2s;
    width: 100%;
  }

  .operation-map-content.loaded {
    opacity: 1;
    pointer-events: auto;
    transform: scale(1);
    transition-delay: 0.3s;
  }

  .operation-map-content.fast-load {
    transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1),
                transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .operation-map-content.fast-load.loaded {
    transition-delay: 0s;
  }

  .company-map-marker {
    align-items: center;
    background: #fff;
    border: 1px solid var(--sgu-border);
    border-radius: var(--pbi-radius);
    box-shadow: var(--pbi-shadow);
    display: flex;
    height: 42px;
    justify-content: center;
    transform: translate(-50%, -50%);
    transition: transform 0.25s ease, box-shadow 0.25s ease;
    width: 42px;
  }

  .company-map-marker.selected {
    animation: markerPulse 1.6s infinite;
    box-shadow: 0 8px 20px rgba(28, 66, 32, 0.45);
    transform: translate(-50%, -50%) scale(1.12);
  }

  .company-map-marker-button {
    all: unset;
    align-items: center;
    border-radius: var(--pbi-radius-sm);
    cursor: pointer;
    display: flex;
    height: 100%;
    justify-content: center;
    width: 100%;
  }

  .company-map-marker-button:focus-visible {
    outline: 2px solid var(--sgu-primary);
    outline-offset: 2px;
  }

  .company-map-marker-img {
    height: 32px;
    object-fit: contain;
    width: 32px;
  }

  .company-map-tooltip {
    background: #fff;
    border: 1px solid var(--sgu-border);
    border-radius: var(--pbi-radius-sm);
    box-shadow: var(--pbi-shadow);
    color: var(--sgu-text);
    left: 50%;
    max-width: 220px;
    padding: 5px 10px;
    position: absolute;
    top: -10px;
    transform: translate(-50%, -100%);
    z-index: 1001;
  }

  .company-map-tooltip strong {
    display: block;
    font-size: 0.78rem;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .map-caption,
  .map-empty {
    align-items: center;
    background: #fff;
    border: 1px solid var(--sgu-border);
    border-radius: var(--pbi-radius-sm);
    box-shadow: var(--pbi-shadow);
    font-size: 0.8125rem;
    bottom: 18px;
    color: var(--sgu-text);
    display: inline-flex;
    gap: 8px;
    left: 18px;
    padding: 10px 14px;
    position: absolute;
    z-index: 5;
  }

  .map-empty {
    bottom: auto;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  .map-empty.in-modal {
    border-radius: var(--pbi-radius);
    height: 300px;
    justify-content: center;
    position: static;
    transform: none;
  }

  .selected-panel,
  .priority-list {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .selected-top {
    align-items: center;
    display: flex;
    gap: 14px;
  }

  .selected-unit-meta {
    color: var(--sgu-text-muted);
    display: block;
    font-size: 0.75rem;
    margin-top: 4px;
  }

  .selected-avatar {
    align-items: center;
    background: var(--sgu-primary-dark);
    border-radius: var(--pbi-radius);
    color: #fff;
    display: flex;
    flex-shrink: 0;
    font-size: 0.875rem;
    font-weight: 600;
    height: 48px;
    justify-content: center;
    width: 48px;
  }

  .status-callout {
    border: 1px solid;
    border-radius: var(--pbi-radius);
    padding: 14px 16px;
  }

  .status-callout span,
  .status-callout small {
    display: block;
    font-weight: 500;
  }

  .status-callout strong {
    color: var(--sgu-text);
    display: block;
    font-size: 1.5rem;
    line-height: 1.1;
    margin: 4px 0;
  }

  .status-callout small {
    color: var(--sgu-text-muted);
    font-size: 0.875rem;
  }

  .dial-row {
    display: grid;
    gap: 14px;
    grid-template-columns: repeat(2, 1fr);
  }

  .mini-dial {
    align-items: center;
    background: conic-gradient(var(--dial-color) var(--progress), #edebe9 0);
    border-radius: 999px;
    display: flex;
    height: 120px;
    justify-content: center;
    margin: auto;
    position: relative;
    width: 120px;
  }

  .mini-dial::before {
    background: #fff;
    border-radius: inherit;
    content: "";
    inset: 12px;
    position: absolute;
  }

  .mini-dial div,
  .mini-dial small {
    position: relative;
    text-align: center;
    z-index: 1;
  }

  .mini-dial div {
    align-items: baseline;
    display: flex;
    gap: 3px;
  }

  .mini-dial strong {
    color: var(--sgu-text);
    font-size: 1.25rem;
    line-height: 1;
  }

  .mini-dial span,
  .mini-dial small {
    color: var(--sgu-text-muted);
  }

  .mini-dial small {
    bottom: 32px;
    font-weight: 500;
    position: absolute;
    text-transform: none;
  }

  .selected-list .list-group-item {
    align-items: center;
    background: #faf9f8;
    border: 0;
    border-bottom: 1px solid #f3f2f1;
    color: var(--sgu-text);
    display: flex;
    justify-content: space-between;
    padding: 10px 0;
  }

  .selected-list .list-group-item:last-child {
    border-bottom: 0;
  }

  .selected-list span {
    align-items: center;
    color: var(--sgu-text-muted);
    display: inline-flex;
    gap: 8px;
  }

  .selected-list strong {
    color: var(--sgu-text);
    font-size: 0.875rem;
    margin-left: 12px;
    text-align: right;
  }

  .chart-wrap {
    height: 260px;
  }

  .company-chart-wrap {
    height: 310px;
  }

  .company-chart-caption {
    color: var(--sgu-text-muted);
    font-size: 0.8125rem;
    margin: 4px 0 0;
  }

  .company-version-legend {
    border-top: 1px solid #f3f2f1;
    list-style: none;
    margin: 12px 0 0;
    max-height: 140px;
    overflow-y: auto;
    padding: 0;
  }

  .company-version-legend li {
    display: grid;
    gap: 2px 10px;
    grid-template-columns: minmax(120px, 34%) 1fr;
    padding: 8px 0;
  }

  .company-version-legend li + li {
    border-top: 1px solid #f3f2f1;
  }

  .company-version-legend strong {
    color: var(--sgu-text);
    font-size: 0.8125rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .company-version-legend strong span {
    color: var(--sgu-primary-dark);
    font-weight: 700;
  }

  .company-version-legend-text {
    color: var(--sgu-text-muted);
    font-size: 0.75rem;
    line-height: 1.35;
  }

  .chart-center {
    left: 50%;
    position: absolute;
    text-align: center;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  .chart-center strong {
    color: var(--sgu-text);
    display: block;
    font-size: 1.5rem;
    font-weight: 600;
  }

  .chart-center span {
    color: var(--sgu-text-muted);
    font-size: 0.875rem;
    font-weight: 500;
    text-transform: none;
  }

  .status-grid {
    display: grid;
    gap: 10px;
    grid-template-columns: repeat(2, 1fr);
    margin-top: 16px;
  }

  .status-chip {
    background: #faf9f8;
    border: 1px solid var(--sgu-border);
    border-radius: var(--pbi-radius-sm);
    color: var(--sgu-text);
    cursor: pointer;
    font-size: 0.8125rem;
    font-weight: 500;
    padding: 8px 10px;
    text-align: left;
    transition: background 0.15s ease;
  }

  .status-chip:hover {
    background: #f3f2f1;
  }

  .status-chip.active {
    background: #fff;
    box-shadow: var(--pbi-shadow);
  }

  .status-chip span {
    display: block;
    font-size: 1.125rem;
    font-weight: 600;
    line-height: 1.1;
    margin-bottom: 2px;
  }

  .priority-item {
    align-items: center;
    background: #fff;
    border: 1px solid var(--sgu-border);
    border-radius: var(--pbi-radius-sm);
    color: var(--sgu-text);
    display: flex;
    gap: 10px;
    padding: 10px 12px;
    width: 100%;
  }

  .priority-dot {
    border-radius: 999px;
    flex-shrink: 0;
    height: 8px;
    width: 8px;
  }

  .priority-list small,
  .equipment-table small {
    color: var(--sgu-text-muted);
    display: block;
    font-size: 0.75rem;
    margin-top: 2px;
  }

  .priority-list .badge {
    margin-left: auto;
  }

  .search-control {
    align-items: center;
    background: #fff;
    border: 1px solid var(--sgu-border);
    border-radius: var(--pbi-radius-sm);
    display: flex;
    gap: 8px;
    min-width: min(100%, 320px);
    padding: 0 12px;
  }

  .search-control input {
    background: transparent;
    border: 0;
    color: var(--sgu-text);
    flex: 1;
    outline: none;
    padding: 11px 0;
  }

  .search-control button {
    background: transparent;
    border: 0;
    color: var(--sgu-text-muted);
  }

  .toolbar-controls .form-select,
  .table-pagination .form-select {
    background-color: #fff;
    border: 1px solid var(--sgu-border);
    border-radius: var(--pbi-radius-sm);
    color: var(--sgu-text);
    min-width: 140px;
  }

  .toolbar-controls .form-select option,
  .table-pagination .form-select option {
    color: var(--sgu-text);
  }

  @media (min-width: 1200px) {
    .map-focus-row .equipment-focus-panel-body {
      overflow-y: auto;
    }
  }

  .table-selection-bar {
    align-items: center;
    background: #fff;
    border: 1px solid var(--sgu-border);
    border-left: 4px solid var(--sgu-primary-dark);
    border-radius: var(--pbi-radius);
    box-shadow: var(--pbi-shadow);
    display: flex;
    flex-wrap: wrap;
    gap: 12px 16px;
    justify-content: space-between;
    margin-top: 16px;
    padding: 12px 16px;
  }

  .table-selection-bar-main {
    align-items: center;
    display: flex;
    flex: 1;
    flex-wrap: wrap;
    gap: 12px 20px;
    min-width: 240px;
  }

  .table-selection-bar-id strong {
    color: var(--sgu-text);
    display: block;
    font-size: 1rem;
  }

  .table-selection-bar-id small {
    color: var(--sgu-text-muted);
    display: block;
    font-size: 0.75rem;
  }

  .table-selection-bar-kicker {
    color: var(--sgu-text-muted);
    display: block;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.02em;
    margin-bottom: 2px;
    text-transform: uppercase;
  }

  .table-selection-bar-metrics {
    color: var(--sgu-text);
    display: flex;
    flex-wrap: wrap;
    font-size: 0.8125rem;
    gap: 10px 16px;
  }

  .table-selection-bar-metrics span {
    align-items: center;
    display: inline-flex;
    white-space: nowrap;
  }

  .table-selection-bar-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .equipment-table-wrap {
    border: 1px solid var(--sgu-border);
    border-radius: var(--pbi-radius);
    margin-top: 12px;
    overflow: auto;
  }

  .equipment-table {
    border-collapse: collapse;
    color: var(--sgu-text);
    font-size: 0.8125rem;
    min-width: 980px;
    width: 100%;
  }

  .equipment-table th {
    background: #faf9f8;
    border-bottom: 1px solid var(--sgu-border);
    color: var(--sgu-text);
    font-size: 0.75rem;
    font-weight: 600;
    padding: 10px 12px;
    position: sticky;
    top: 0;
    text-transform: none;
    z-index: 1;
  }

  .equipment-table td {
    border-bottom: 1px solid #f3f2f1;
    padding: 10px 12px;
    vertical-align: middle;
  }

  .equipment-table tbody tr {
    cursor: pointer;
    transition: background 0.12s ease;
  }

  .equipment-table tbody tr:nth-child(even) {
    background: #faf9f8;
  }

  .equipment-table tbody tr:hover {
    background: #f3f2f1;
  }

  .equipment-table tbody tr.is-selected {
    background: #e8f4e8 !important;
    box-shadow: inset 3px 0 0 var(--sgu-primary-dark);
  }

  .equipment-table .status-cell {
    min-width: 108px;
    white-space: nowrap;
  }

  .status-pill {
    align-items: center;
    border: 1px solid transparent;
    border-radius: var(--pbi-radius-sm);
    display: inline-flex;
    font-size: 0.75rem;
    font-weight: 600;
    gap: 6px;
    line-height: 1.2;
    padding: 5px 10px 5px 8px;
  }

  .status-pill-dot {
    border-radius: 999px;
    flex-shrink: 0;
    height: 8px;
    width: 8px;
  }

  .status-pill--ok {
    background: #edf7ed;
    border-color: #b8dfc4;
    color: #1a6b31;
  }

  .status-pill--ok .status-pill-dot {
    background: #28a745;
  }

  .status-pill--attention {
    background: #fff8e6;
    border-color: #f0d48a;
    color: #7a5f00;
  }

  .status-pill--attention .status-pill-dot {
    background: #e6a800;
  }

  .status-pill--danger {
    background: #fff3e8;
    border-color: #ffc89a;
    color: #a34f00;
  }

  .status-pill--danger .status-pill-dot {
    background: #ff8c00;
  }

  .status-pill--nodata {
    background: #f3f2f1;
    border-color: #d2d0ce;
    color: #605e5c;
  }

  .status-pill--nodata .status-pill-dot {
    background: #8a8886;
  }

  .resource-dots {
    display: flex;
    gap: 8px;
  }

  .resource-dots span {
    align-items: center;
    background: rgba(28, 66, 32, 0.07);
    border-radius: 999px;
    color: var(--sgu-text-muted);
    display: inline-flex;
    height: 30px;
    justify-content: center;
    width: 30px;
  }

  .resource-dots span.active {
    background: rgba(143,173,21,0.16);
    color: var(--sgu-primary-dark);
  }

  .table-pagination {
    align-items: center;
    color: var(--sgu-text-muted);
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: space-between;
    margin-top: 16px;
  }

  .table-pagination > div {
    align-items: center;
    display: flex;
    gap: 8px;
  }

  .empty-state {
    align-items: center;
    color: var(--sgu-text-muted);
    display: flex;
    gap: 10px;
    justify-content: center;
    min-height: 120px;
    padding: 20px;
    text-align: center;
  }

  .cockpit-modal {
    background: #fff;
    border: 1px solid var(--sgu-border);
    border-radius: var(--pbi-radius);
    color: var(--sgu-text);
    overflow: hidden;
  }

  .cockpit-modal .modal-header,
  .cockpit-modal .modal-footer {
    background: #faf9f8;
    border-color: var(--sgu-border);
    padding: 12px 16px;
  }

  .cockpit-modal .modal-title {
    font-size: 1rem;
    font-weight: 600;
  }

  .cockpit-modal .btn-close {
    filter: none;
  }

  .modal-dark-card {
    background: #fff;
    border: 1px solid var(--sgu-border);
    border-radius: var(--pbi-radius);
    color: var(--sgu-text);
    padding: 12px;
  }

  .modal-dark-card h5 {
    color: var(--sgu-text);
    font-size: 0.9375rem;
    font-weight: 600;
    margin-bottom: 12px;
  }

  .modal-search {
    border: 1px solid var(--sgu-border);
    border-radius: var(--pbi-radius-sm);
    font-size: 0.8125rem;
    margin-bottom: 12px;
    padding: 8px 12px;
    width: 100%;
  }

  .modal-equipment-list .list-group-item {
    align-items: center;
    background: #fff;
    border-color: var(--sgu-border);
    color: var(--sgu-text);
    display: flex;
    justify-content: space-between;
  }

  .modal-equipment-list small {
    color: var(--sgu-text-muted);
    display: block;
  }

  @keyframes markerPulse {
    0% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--marker), transparent 42%), 0 0 24px var(--marker); }
    70% { box-shadow: 0 0 0 13px color-mix(in srgb, var(--marker), transparent 100%), 0 0 24px var(--marker); }
    100% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--marker), transparent 100%), 0 0 24px var(--marker); }
  }

  @media (max-width: 992px) {
    .pbi-report-header,
    .table-toolbar,
    .map-header,
    .pbi-slicer-bar {
      flex-direction: column;
      align-items: stretch;
    }

    .pbi-report-actions,
    .toolbar-controls {
      justify-content: flex-start;
      width: 100%;
    }

    .pbi-slicer-meta {
      margin-left: 0;
    }

    .search-control,
    .pbi-btn-export {
      width: 100%;
    }

    .table-selection-bar {
      align-items: stretch;
      flex-direction: column;
    }

    .table-selection-bar-actions {
      width: 100%;
    }

    .table-selection-bar-actions .btn {
      flex: 1;
    }

    .map-focus-row .operation-map {
      flex: none;
      height: 420px;
    }
  }

  @media (max-width: 576px) {
    .company-cockpit {
      padding: 12px;
    }

    .dial-row,
    .status-grid {
      grid-template-columns: 1fr;
    }

    .mini-dial {
      height: 132px;
      width: 132px;
    }
  }
`;

export default DashboardCompany;
